import React, { useState, useEffect, useRef, useCallback } from "react";
import { User } from "@supabase/supabase-js";
import Logo from "./Logo";
import AuthModal from "./AuthModal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";

interface GlobalHeaderProps {
  user: User | null;
  profile: { avatar_url: string | null; avatar_url_200: string | null; avatar_url_80: string | null; display_name: string | null } | null;
  onLogout: () => void;
  shouldRetract?: boolean;
}

const GlobalHeader: React.FC<GlobalHeaderProps> = ({ user, profile, onLogout, shouldRetract = false }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  
  useEffect(() => {
    // Delay header slide-in
    const timer = setTimeout(() => setHasMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollYRef = useRef(0);

  const getInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const resetHideTimer = useCallback(() => {
    clearHideTimer();

    hideTimerRef.current = setTimeout(() => {
      console.log("🕐 Timer fired - hiding header");
      setIsVisible(false);
    }, 3000); // Hide after 3 seconds of inactivity
  }, [clearHideTimer]);

  const showHeader = useCallback(() => {
    setIsVisible(true);
    resetHideTimer();
  }, [resetHideTimer]);

  useEffect(() => {
    // If shouldRetract prop is true, hide the header after delay
    if (shouldRetract) {
      console.log("🎯 shouldRetract is true - starting hide timer");
      resetHideTimer();
    } else {
      // If shouldRetract is false, show the header immediately
      console.log("🎯 shouldRetract is false - showing header");
      setIsVisible(true);
      clearHideTimer();
    }

    const handleScroll = () => {
      if (!shouldRetract) return; // Only handle scroll when header can retract

      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - lastScrollYRef.current;

      console.log("📜 Scroll:", { currentScrollY, lastScroll: lastScrollYRef.current, diff: scrollDiff, isVisible });

      if (scrollDiff < -10) {
        // Scrolling up - show and reset timer
        console.log("⬆️ Scrolling up - showing header, resetting timer");
        showHeader();
      } else if (scrollDiff > 10) {
        // Scrolling down - hide immediately
        console.log("⬇️ Scrolling down - hiding header immediately");
        setIsVisible(false);
        clearHideTimer();
      }

      lastScrollYRef.current = currentScrollY;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!shouldRetract) return; // Only handle mouse when header can retract

      // Show header on mouse move if currently hidden and mouse is near top
      if (!isVisible && e.clientY < 15) {
        console.log("🖱️ Mouse near top - showing header");
        showHeader();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
      clearHideTimer();
    };
  }, [isVisible, showHeader, resetHideTimer, clearHideTimer, shouldRetract]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[60] bg-background shadow-elegant border-b border-border/50 
        transition-all duration-1000 ease-in-out 
        ${hasMounted ? (isVisible ? "translate-y-0" : "-translate-y-[calc(100%-8px)]") : "-translate-y-full"}
        ${!hasMounted ? "animate-slide-in-down" : ""}`}
    >
      <div className="flex items-center justify-between px-6 py-2">
        <div className="flex items-center gap-3">
          <Logo size={40} />
          <h1 className="text-xl font-semibold text-foreground">Carline Consensor</h1>
        </div>

        <div className="flex items-center gap-4">
          {!user && (
            <button
              onClick={() => setShowAuthModal(true)}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-md transition-colors"
            >
              <span className="md:hidden">Sign In</span>
              <span className="hidden md:inline">Sign In / Sign Up</span>
            </button>
          )}

          {user && (
            <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
              <AlertDialogTrigger asChild>
                <button className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full">
                  <Avatar className="h-10 w-10 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all">
                    <AvatarImage 
                      src={profile?.avatar_url_80 || profile?.avatar_url_200 || profile?.avatar_url || undefined} 
                      alt={profile?.display_name || "User"} 
                    />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Log Out</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to log out?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={onLogout}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Log Out
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </header>
  );
};

export default GlobalHeader;
